import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, map } from 'rxjs';

import { APP_CONFIG } from '../config/app-config';
import type { ApiEnvelope } from '../http/api-envelope';
import type { NotificationResponse, NotificationStatusRequest } from './notifications.contract';
import { toNotification } from './notifications.mapper';
import type { AppNotification, NotificationStatus } from './notifications.models';
import { toApiPath } from './notifications.utils';

@Injectable()
export abstract class NotificationsApi {
  abstract list(): Observable<readonly AppNotification[]>;
  abstract setStatus(id: string, status: NotificationStatus): Observable<void>;
  /** Calls an action's URL. Invitation answers are PUTs; older actions are GETs. */
  abstract runAction(url: string, method?: 'GET' | 'PUT'): Observable<void>;
}

@Injectable()
export class HttpNotificationsApi extends NotificationsApi {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_CONFIG);
  private readonly base = '/communications/messages/notifications';

  override list(): Observable<readonly AppNotification[]> {
    return this.http
      .get<ApiEnvelope<readonly NotificationResponse[] | null>>(this.base)
      .pipe(map((response) => (response.data ?? []).map(toNotification)));
  }

  override setStatus(id: string, status: NotificationStatus): Observable<void> {
    const body: NotificationStatusRequest = { status };

    return this.http
      .put<unknown>(`${this.base}/${encodeURIComponent(id)}`, body)
      .pipe(map(() => undefined));
  }

  override runAction(url: string, method: 'GET' | 'PUT' = 'GET'): Observable<void> {
    const path = toApiPath(url, this.config.apiUrl);
    const request$ =
      method === 'PUT' ? this.http.put<unknown>(path, {}) : this.http.get<unknown>(path);

    return request$.pipe(map(() => undefined));
  }
}
